import React, { useState, useEffect, useMemo } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    Space,
    Input,
    Typography,
    Tag,
    Button,
    Drawer,
    Descriptions,
    Select,
    Alert,
    Row,
    Col,
    Statistic,
    message,
} from 'antd';
import {
    SearchOutlined,
    EyeOutlined,
    ReloadOutlined,
    StarFilled,
    LinkOutlined,
    DownloadOutlined,
    CrownOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { registrarService } from '../../services/registrarService';
import moment from 'moment';

import { adminPath } from '../../../shared/config/paths';

const { Title, Text, Paragraph } = Typography;

/**
 * Admin: 联系人模板管理（只读）
 *
 * 决策：admin 端不提供「系统模板」概念 —— 所有模板按用户隔离，admin 只能看，
 * 不能改。后端 `ContactAdminController` 仅暴露 getIndex/getGet，对应于此 UI。
 * (参考 ADMIN_DOMAIN_UX_FINDINGS.md 第 5 节)
 *
 * 用户端 CRUD 走 /api/user/domain-contacts，由用户自己管理。
 */
const ContactTemplates = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // 客户端筛选
    const [countryFilter, setCountryFilter] = useState(undefined);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async (params = {}) => {
        setLoading(true);
        try {
            const response = await registrarService.getAdminContactTemplates(params);
            if (response.success) {
                setTemplates(response.data || []);
            }
        } catch (error) {
            message.error('加载失败');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        if (!value) {
            delete newFilters[key];
        }
        setFilters(newFilters);
        loadTemplates(newFilters);
    };

    const handleViewDetail = (record) => {
        setSelectedTemplate(record);
        setDrawerVisible(true);
    };

    const viewUserDomains = (userId) => {
        navigate(adminPath(`/domain-registration/users?user_id=${userId}`));
    };

    // 客户端国家筛选
    const filteredTemplates = useMemo(() => {
        if (!countryFilter) return templates;
        return templates.filter((t) => t.country === countryFilter);
    }, [templates, countryFilter]);

    // 国家列表（来自当前模板集）
    const countries = useMemo(() => {
        const set = new Set();
        templates.forEach((t) => t.country && set.add(t.country));
        return Array.from(set).sort();
    }, [templates]);

    // 每用户模板数 -> 用于侧面统计
    const userCounts = useMemo(() => {
        const counts = {};
        templates.forEach((t) => {
            counts[t.user_id] = (counts[t.user_id] || 0) + 1;
        });
        return counts;
    }, [templates]);

    const exportCsv = () => {
        const rows = filteredTemplates;
        if (rows.length === 0) {
            message.warning('当前筛选无可导出数据');
            return;
        }
        const headers = ['id', 'user_id', 'username', 'template_name', 'is_default',
            'first_name', 'last_name', 'email', 'phone', 'country', 'created_at'];
        const lines = [headers.join(',')];
        rows.forEach((t) => {
            const cols = [t.id, t.user_id, t.username || '', t.template_name || '',
                t.is_default ? '是' : '否',
                t.first_name || '', t.last_name || '',
                t.email || '', t.phone || '', t.country || '',
                t.created_at || ''];
            lines.push(cols.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `domain-contacts-${moment().format('YYYYMMDD-HHmmss')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        message.success(`已导出 ${rows.length} 条联系人模板`);
    };

    const columns = [
        {
            title: '用户',
            key: 'user',
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <a onClick={() => viewUserDomains(record.user_id)}>
                        {record.username || `User #${record.user_id}`}
                    </a>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        ID: {record.user_id} · {userCounts[record.user_id]} 模板
                    </Text>
                </Space>
            ),
        },
        {
            title: '模板名称',
            dataIndex: 'template_name',
            key: 'template_name',
            render: (name, record) => (
                <Space>
                    <span style={{ fontWeight: 500 }}>{name || '(未命名)'}</span>
                    {record.is_default && (
                        <Tag color="gold" icon={<StarFilled />}>默认</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: '姓名',
            key: 'name',
            render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim() || '-',
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: '电话',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: '国家',
            dataIndex: 'country',
            key: 'country',
            width: 80,
            render: (c) => c ? <Tag><CountryFlag code={c} />{c}</Tag> : '-',
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 120,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
            sorter: (a, b) => moment(a.created_at || 0).diff(moment(b.created_at || 0)),
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                        data-testid="contact-view-btn"
                    >
                        查看
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => viewUserDomains(record.user_id)}
                    >
                        域名
                    </Button>
                </Space>
            ),
        },
    ];

    // 统计
    const stats = useMemo(() => {
        const uniqueUsers = new Set(filteredTemplates.map((t) => t.user_id)).size;
        const defaults = filteredTemplates.filter((t) => t.is_default).length;
        return {
            total: filteredTemplates.length,
            uniqueUsers,
            defaults,
        };
    }, [filteredTemplates]);

    return (
        <div>
            <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                icon={<CrownOutlined />}
                banner
                message={
                    <span>
                        <Text strong>管理员视图（只读）</Text> — 这是所有用户的域名联系人模板汇总。
                        模板归属用户，admin 无法在此修改 / 删除（按设计）。
                    </span>
                }
            />

            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col xs={12} sm={8}>
                    <Card size="small"><Statistic title="模板总数" value={stats.total} prefix={<UserOutlined />} /></Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card size="small"><Statistic title="覆盖用户数" value={stats.uniqueUsers} /></Card>
                </Col>
                <Col xs={12} sm={8}>
                    <Card size="small"><Statistic title="默认模板数" value={stats.defaults} valueStyle={{ color: '#faad14' }} /></Card>
                </Col>
            </Row>

            <Card
                title={<Title level={4} style={{ margin: 0 }}>联系人模板管理</Title>}
                bordered={false}
                extra={
                    <Space>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportCsv}
                            data-testid="contact-export-csv"
                        >
                            导出CSV
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => loadTemplates(filters)}
                            loading={loading}
                        >
                            刷新
                        </Button>
                    </Space>
                }
            >
                <Space wrap style={{ marginBottom: 12 }}>
                    <Input
                        placeholder="用户ID"
                        style={{ width: 120 }}
                        allowClear
                        onChange={(e) => handleFilterChange('user_id', e.target.value)}
                    />
                    <Input
                        placeholder="搜索邮箱"
                        prefix={<SearchOutlined />}
                        style={{ width: 200 }}
                        allowClear
                        onChange={(e) => handleFilterChange('email', e.target.value)}
                    />
                    <Select
                        placeholder="国家"
                        style={{ width: 140 }}
                        allowClear
                        value={countryFilter}
                        onChange={setCountryFilter}
                        showSearch
                        optionFilterProp="search"
                        options={countries.map((c) => ({ value: c, search: c, label: <><CountryFlag code={c} />{c}</> }))}
                    />
                </Space>

                <Table
                    columns={columns}
                    dataSource={filteredTemplates}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1400 }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                />
            </Card>

            <Drawer
                title="联系人模板详情"
                placement="right"
                width={600}
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                extra={
                    selectedTemplate && (
                        <Button
                            icon={<LinkOutlined />}
                            onClick={() => viewUserDomains(selectedTemplate.user_id)}
                        >
                            查看该用户的域名
                        </Button>
                    )
                }
            >
                {selectedTemplate && (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="只读视图 — 模板归属用户，admin 无法在此修改"
                        />
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label="用户ID">
                                <a onClick={() => viewUserDomains(selectedTemplate.user_id)}>
                                    {selectedTemplate.user_id}
                                </a>
                            </Descriptions.Item>
                            <Descriptions.Item label="用户名">
                                {selectedTemplate.username || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="模板名称">
                                <Space>
                                    {selectedTemplate.template_name}
                                    {selectedTemplate.is_default && (
                                        <Tag color="gold" icon={<StarFilled />}>默认</Tag>
                                    )}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="名字">
                                {selectedTemplate.first_name}
                            </Descriptions.Item>
                            <Descriptions.Item label="姓氏">
                                {selectedTemplate.last_name}
                            </Descriptions.Item>
                            <Descriptions.Item label="邮箱">
                                {selectedTemplate.email}
                            </Descriptions.Item>
                            <Descriptions.Item label="电话">
                                {selectedTemplate.phone}
                            </Descriptions.Item>
                            <Descriptions.Item label="地址">
                                {selectedTemplate.address}
                            </Descriptions.Item>
                            <Descriptions.Item label="城市">
                                {selectedTemplate.city}
                            </Descriptions.Item>
                            <Descriptions.Item label="州/省">
                                {selectedTemplate.state || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="邮编">
                                {selectedTemplate.postal_code}
                            </Descriptions.Item>
                            <Descriptions.Item label="国家代码">
                                <CountryFlag code={selectedTemplate.country} />{selectedTemplate.country}
                            </Descriptions.Item>
                            <Descriptions.Item label="创建时间">
                                {selectedTemplate.created_at ? moment(selectedTemplate.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="更新时间">
                                {selectedTemplate.updated_at ? moment(selectedTemplate.updated_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default ContactTemplates;
